import { useEffect } from 'react';
import { ProgressBar } from '@app/components/shared/ProgressBar';
import { OrderSummary } from '@app/components/shared/OrderSummary';
import { buttonStyles, pageStyles } from '@/styles/sharedStyles';

interface PaymentSuccessProps {
  stepData?: any;
  flowType?: 'one-off' | 'subscription';
  onNext?: () => void;
}

export function PaymentSuccess({ stepData, flowType = 'one-off', onNext }: PaymentSuccessProps) {
  // Prevent back navigation after successful payment
  // Also prevent page refresh/exit since user hasn't completed the flow yet
  // and we cannot guarantee localStorage persistence or state recovery
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Prevent going back by pushing the current state again
      // This prevents users from navigating back to payment step and potentially paying again
      window.history.pushState(null, '', window.location.pathname + window.location.search);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Warn user before leaving since flow is not complete yet
      // Losing this state could cause issues as we can't reliably recover it
      event.preventDefault();
      // Only preventDefault is sufficient in modern browsers
    };

    // Add current state to history and set up listeners
    window.history.pushState(null, '', window.location.pathname + window.location.search);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="max-w-7xl w-full mx-auto md:mb-10">
      <div className="mb-8">
        <ProgressBar currentStep={2} totalSteps={4} />
      </div>

      <div className="grid lg:grid-cols-[350px_1fr] gap-8">
        <OrderSummary flowType={flowType} stepData={stepData} showSecure={true} />

        <div className="bg-white rounded-3xl shadow-sm p-8 flex flex-col justify-center">
          <div className="text-center space-y-8">
            {/* Success Icon */}
            <div className="mx-auto w-20 h-20 bg-green-light/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            {/* Success Message */}
            <div className="space-y-4">
              <h1 className={pageStyles.title}>Payment Successful</h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg mx-auto">
                Next is the fun part! Tell us who you're shopping for, and we'll take it from there.
              </p>
            </div>

            {/* Next Button */}
            {onNext && (
              <button 
                className={buttonStyles.primary}
                onClick={onNext}
              >
                NEXT →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
